import type { ReactNode } from "react";
import { DetailGrid, DetailSection, EmptyState, MetricCard, StatusBadge } from "./ui";
import {
  channelBasis,
  channelDecision,
  channelsLabel,
  consumerName,
  decisionGuidance,
  decisionLabels,
  decisionTone,
  dncChannels,
  formatDncDate,
  overallDecision,
  recordStatusLabel,
  scopeLabels,
  sourceCategoryLabels,
  sourceTypeLabel,
  associateLabel,
  suppressionBasis,
  type DncAuditEvent,
  type DncEligibility,
  type DncRecord,
  type DncSummary,
} from "./dncModel";

export function DncNotActive({ reason }: { reason: "disabled" | "unavailable" }): JSX.Element {
  return reason === "disabled" ? (
    <EmptyState
      title="DNC compliance is not active in this environment."
      detail="Checks and records will be available once DNC compliance is enabled for this environment."
    />
  ) : (
    <EmptyState
      title="No compliance data is currently available."
      detail="DNC records could not be loaded. No figures are shown until the data is available."
    />
  );
}

const decisionPanel: Record<ReturnType<typeof overallDecision>, string> = {
  CLEAR: "border-l-emerald-600",
  DO_NOT_CONTACT: "border-l-rose-600",
  REVIEW_REQUIRED: "border-l-amber-500",
};

const decisionText: Record<ReturnType<typeof overallDecision>, string> = {
  CLEAR: "text-emerald-700",
  DO_NOT_CONTACT: "text-rose-700",
  REVIEW_REQUIRED: "text-amber-800",
};

export function CheckResultPanel({ result, identifier }: { result: DncEligibility; identifier: string }): JSX.Element {
  const decision = overallDecision(result);
  const basis = decision === "DO_NOT_CONTACT" ? suppressionBasis(result) : null;
  const adminDetail = result.associateScopedSuppressions;
  return (
    <section
      role="status"
      aria-live="polite"
      data-decision={decision}
      className={`rounded-lg border border-l-4 border-slate-200 bg-white p-4 ${decisionPanel[decision]}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Result for {identifier}</div>
          <div className={`mt-1 text-2xl font-bold tracking-tight ${decisionText[decision]}`}>{decisionLabels[decision]}</div>
          {basis ? <div className="mt-1 text-sm font-semibold text-slate-800">{basis}</div> : null}
          <p className="mt-1 text-sm text-slate-600">{decisionGuidance[decision]}</p>
        </div>
        <StatusBadge label={sourceCategoryLabels[result.source] ?? "Review"} tone={decision === "CLEAR" ? "neutral" : decisionTone[decision]} />
      </div>

      <dl className="mt-4 grid gap-2 sm:grid-cols-2">
        {dncChannels.map(({ value, label }) => {
          const channel = result.channels[value];
          const channelState = channelDecision(channel);
          const detail = channelBasis(channel);
          return (
            <div key={value} data-channel={value} className="flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <dt className="text-sm font-semibold text-slate-700">{label}</dt>
              <dd className="text-right">
                <StatusBadge label={decisionLabels[channelState]} tone={decisionTone[channelState]} />
                {detail ? <div className="mt-1 text-[11px] text-slate-500">{detail}</div> : null}
                {channel?.status === "SUPPRESSED" && channel.recordedAt ? <div className="text-[11px] text-slate-500">Recorded {formatDncDate(channel.recordedAt)}</div> : null}
              </dd>
            </div>
          );
        })}
      </dl>

      {adminDetail && adminDetail.length > 0 ? (
        <div className="mt-4" data-testid="dnc-admin-scope-detail">
          <DetailSection title="Compliance detail: associate-specific preferences">
            <ul className="space-y-1 text-sm text-slate-700">
              {adminDetail.map((item) => (
                <li key={`${item.targetAgentKwuid}-${item.channel}`}>
                  Agent KWUID {item.targetAgentKwuid} · {channelsLabel([item.channel])} · {formatDncDate(item.recordedAt)}
                </li>
              ))}
            </ul>
          </DetailSection>
        </div>
      ) : null}
    </section>
  );
}

export function DncOverviewCards({ summary }: { summary: DncSummary }): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <MetricCard label="Internal suppressions" value={summary.internalSuppressions ?? 0} detail="Identifiers with an active internal preference" />
      <MetricCard label="KWSA-wide" value={summary.kwsaWideSuppressions ?? 0} detail="Apply to every associate" />
      <MetricCard label="Associate-specific" value={summary.associateSpecificSuppressions ?? 0} detail="Apply to one associate only" />
      <MetricCard
        label="National Registry"
        value={summary.nationalRegistryConnected ? summary.nationalRegistrySuppressions ?? 0 : "Not connected"}
        detail={summary.nationalRegistryConnected ? "Identifiers on the latest registry return" : "No registry return has been processed"}
      />
      <MetricCard label="Review required" value={summary.reviewRequired ?? 0} detail="Records needing compliance review" tone={(summary.reviewRequired ?? 0) > 0 ? "warning" : "default"} />
    </div>
  );
}

const th = "px-3 py-3";
const td = "px-3 py-3";

export function DncRecordsTable({ records, onOpen }: { records: DncRecord[]; onOpen: (record: DncRecord) => void }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[900px] w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className={th}>Date</th>
            <th className={th}>Consumer</th>
            <th className={th}>Identifier</th>
            <th className={th}>Scope</th>
            <th className={th}>Channels</th>
            <th className={th}>Source</th>
            <th className={th}>Market Centre</th>
            <th className={th}>Status</th>
            <th className={th}><span className="sr-only">Action</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {records.map((record) => {
            const status = recordStatusLabel(record.action);
            return (
              <tr key={record.record_id} className="align-top hover:bg-slate-50">
                <td className={`whitespace-nowrap text-slate-600 ${td}`}>{formatDncDate(record.recorded_at)}</td>
                <td className={`font-semibold text-slate-900 ${td}`}>{consumerName(record.consumer_first_name, record.consumer_last_name)}</td>
                <td className={`break-all text-slate-600 ${td}`}>{(record.identifiers ?? []).join(", ") || "Not recorded"}</td>
                <td className={td}><StatusBadge label={record.suppression_scope === "ASSOCIATE" ? "Associate-specific" : "KWSA-wide"} tone={record.suppression_scope === "ASSOCIATE" ? "info" : "neutral"} /></td>
                <td className={`text-slate-700 ${td}`}>{channelsLabel(record.channels)}</td>
                <td className={`text-slate-700 ${td}`}>{record.source_category === "NATIONAL_REGISTRY" ? "National Registry" : sourceTypeLabel(record.source_type)}</td>
                <td className={`text-slate-700 ${td}`}>{record.source_market_centre_name ?? (record.source_market_centre_id ? `MC ${record.source_market_centre_id}` : "Not attributed")}</td>
                <td className={td}><StatusBadge label={status.label} tone={status.tone} /></td>
                <td className={td}>
                  <button type="button" onClick={() => onOpen(record)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DncAuditTable({ events }: { events: DncAuditEvent[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[900px] w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className={th}>Recorded</th>
            <th className={th}>Consumer / Identifier</th>
            <th className={th}>Event</th>
            <th className={th}>Scope</th>
            <th className={th}>Origin</th>
            <th className={th}>Captured by</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {events.map((event) => (
            <tr key={event.id} className="align-top hover:bg-slate-50">
              <td className={`whitespace-nowrap text-slate-600 ${td}`}>{formatDncDate(event.recorded_at)}</td>
              <td className={td}>
                <div className="font-semibold text-slate-900">{consumerName(event.consumer_first_name, event.consumer_last_name)}</div>
                <div className="mt-0.5 break-all text-xs text-slate-500">{event.raw_identifier ?? event.normalized_identifier ?? "No identifier"}</div>
              </td>
              <td className={td}>
                <div className="font-semibold text-slate-800">{event.action} · {channelsLabel(event.channel ? [event.channel] : null)}</div>
                <div className="mt-0.5 text-xs text-slate-500">{event.dnc_type === "NCC" ? "National Registry" : "Internal"}{event.reason ? ` · ${event.reason}` : ""}</div>
              </td>
              <td className={`text-slate-700 ${td}`}>{event.suppression_scope === "ASSOCIATE" ? `Associate-specific (KWUID ${event.target_agent_kwuid ?? "unknown"})` : "KWSA-wide"}</td>
              <td className={`text-xs text-slate-600 ${td}`}>
                {sourceTypeLabel(event.source_type)}
                <br />
                {associateLabel(event.source_agent_name, event.source_agent_kwuid)}
                <br />
                {event.source_market_centre_id ? `Market Centre ${event.source_market_centre_id}` : "No Market Centre attribution"}
              </td>
              <td className={`text-xs text-slate-600 ${td}`}>{event.captured_by_user_email ?? (event.captured_by_associate_id ? `Associate ${event.captured_by_associate_id}` : "Consumer request")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DncRecordDrawer({
  record,
  currentStatus,
  history,
  onClose,
}: {
  record: DncRecord;
  currentStatus: ReactNode;
  history: ReactNode;
  onClose: () => void;
}): JSX.Element {
  const status = recordStatusLabel(record.action);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30" role="presentation" onMouseDown={onClose}>
      <aside
        className="h-full w-full max-w-2xl overflow-y-auto bg-white p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dnc-record-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge label={status.label} tone={status.tone} />
              <StatusBadge label={record.suppression_scope === "ASSOCIATE" ? "Associate-specific" : "KWSA-wide"} tone="info" />
            </div>
            <h2 id="dnc-record-title" className="mt-2 text-xl font-semibold text-slate-900">
              {consumerName(record.consumer_first_name, record.consumer_last_name)}
            </h2>
            <p className="mt-1 text-xs text-slate-500">Recorded {formatDncDate(record.recorded_at)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Close
          </button>
        </div>

        <div className="mt-5 space-y-5">
          <DetailSection title="Summary">
            <DetailGrid items={[
              ["Status", status.label],
              ["Source", record.source_category === "NATIONAL_REGISTRY" ? "National Registry" : sourceTypeLabel(record.source_type)],
              ["Reason", record.reason || "No reason supplied"],
            ]} />
          </DetailSection>
          <DetailSection title="Current Status">{currentStatus}</DetailSection>
          <DetailSection title="Identifiers">
            <DetailGrid items={(record.identifiers ?? []).map((identifier, index) => [`Identifier ${index + 1}`, identifier] as [string, string])} />
          </DetailSection>
          <DetailSection title="Suppression Scope">
            <DetailGrid items={[
              ["Scope", scopeLabels[record.suppression_scope]],
              ["Applies to", record.suppression_scope === "ASSOCIATE" ? `Agent KWUID ${record.target_agent_kwuid ?? "unknown"} only` : "Every KWSA associate"],
            ]} />
          </DetailSection>
          <DetailSection title="Channels">
            <p className="text-sm text-slate-800">{channelsLabel(record.channels)}</p>
          </DetailSection>
          <DetailSection title="Source / Attribution">
            <DetailGrid items={[
              ["Origin", sourceTypeLabel(record.source_type)],
              ["Origin agent", associateLabel(record.source_agent_name, record.source_agent_kwuid)],
              ["Market Centre", record.source_market_centre_name ?? record.source_market_centre_id ?? "Not attributed"],
            ]} />
          </DetailSection>
          <DetailSection title="Audit History">{history}</DetailSection>
        </div>
      </aside>
    </div>
  );
}
